import React, { useEffect, useRef, useState } from 'react';
import { UseBoolean } from 'react-hanger';
import { useLocation, useNavigate } from 'react-router';
import { TransitionGroup } from 'react-transition-group';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { List } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import ListItemText from '@mui/material/ListItemText';
import { SvgIcon } from '@progress/kendo-react-common';
import { InputPrefix, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { searchIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import { makeStyles } from 'tss-react/mui';
import LogoLightgrayBackgroundImage from '../../../../public/img/logo-lightgray-bg.png';
import LogoImage from '../../../../public/img/logo.png';
import OpraIcon from '../../../../public/img/opra.svg?react';
import styles from './style.module.scss';
import { TextBox } from '@components-clean/core/text-box';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { parsePathnameToActiveMenuInfo } from '@components-clean/page-nav/page-nav-menu-active-menu-info';
import { PageNavMenuItem, PageNavMenuItemWithSubMenu } from '@components-clean/page-nav/page-nav-menu-item';
import PageNavMenuItemIcon from '@components-clean/page-nav/page-nav-menu-item-icon';
import navMenuItemStyles from '@components-clean/page-nav/page-nav-menu-item.module.scss';
import {
    filterPageNavItems,
    getActiveItemsAfterItemChanged,
} from '@components-clean/page-nav/page-nav-menu-item.utils';
import { pageNavItems, PageNavItem } from '@components-clean/page-nav/page-nav-menu-items.data';
import PageNavTooltip from '@components-clean/page-nav/page-nav-tooltip';
import ReseedData from '@components-clean/page-nav/reseed-data';
import StyledMuiListItem from '@components-clean/page-nav/styled-list-item';
import { ActiveItemPerLevelInfo } from '@components-clean/page-nav/types';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import LocalStorage from '@utils-clean/local-storage-service';
import PageNavFooter from 'views-clean/components/page-nav/page-nav-footer';
import { DataViewConfigItem } from '@contexts/local-state-context';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { AllRoutes } from '@utils/router/all-routes';
import { GET_DATA_VIEW_CONFIGURATION_NAMES, GET_STUDY_HAS_DATASETS } from '@queries/central-monitoring';
import {
    studyHasDataSets,
    studyHasDataSetsVariables,
    dataViewConfigurationNames,
    dataViewConfigurationNamesVariables,
} from '@queries/generated/central-monitoring.generated';
import { studyUsingCM2, studyUsingCM2Variables } from '@queries/generated/studies.generated';
import { GET_STUDY_USING_CM2 } from '@queries/studies';

const PageNav = ({ tabletMode, isCollapsed }: { tabletMode: boolean; isCollapsed: UseBoolean }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAuthorization();
    const { state, dispatch } = useLocalState();
    const {
        confirmDiscardChanges,
        confirmDiscardChangesLegacy,
        discardChanges,
        anyCentralMonitoringToolkitHasChanges,
        anySubjectMonitoringToolkitHasChanges,
        anyRiskAssessmentToolkitHasChanges,
    } = useUnsavedChangesContext();

    // TODO - remove after testing
    if (user !== null) {
        const shouldThrow = LocalStorage.getItem(user!, 'ForceNavError');
        const isDev = [
            Constants.environment.LOCAL,
            Constants.environment.DEV,
            Constants.environment.DEV1,
            Constants.environment.DEV2,
            Constants.environment.TEST,
            Constants.environment.TEST2,
        ].includes(window.environment);
        if (shouldThrow && isDev) {
            throw new Error(shouldThrow);
        }
    }

    const drawerExpandedWidth = 260;
    const drawerCollapsedWidth = 60;
    const useStyles = makeStyles()((theme) => ({
        drawer: {
            width: drawerExpandedWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
        },
        drawerExpanded: {
            width: drawerExpandedWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        drawerCollapsed: {
            width: drawerCollapsedWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
        },
    }));

    const { classes: muiStyles } = useStyles();

    const studyRef = state?.selectedStudy?.id as string;
    const [loadConfigs, { data: configs, loading }] = useLazyQuery<
        dataViewConfigurationNames,
        dataViewConfigurationNamesVariables
    >(GET_DATA_VIEW_CONFIGURATION_NAMES, { fetchPolicy: 'network-only' });

    const dataViewNavigations: PageNavItem[] = [
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...(configs?.dataViewConfigurations || [])
            ?.filter((x) => x && x.inUse === true)
            .sort((a, b) => {
                return a!.displayIndex! - b!.displayIndex!;
            })
            ?.flatMap<PageNavItem>((config, index) => {
                return config
                    ? [
                          {
                              index,
                              label: config.name || '',
                              key: 'cm2-data-views',
                              moduleValue: Constants.Modules.CM2,
                              element: Constants.PageElements.DataViewsCM2,
                              params: { dataView: config.id as string },
                          },
                      ]
                    : [];
            }),
    ];

    const navigateToRoute = (itemId: string, params?: Record<string, string>) => {
        const routePath = AllRoutes.path(itemId, params);

        if (routePath) {
            navigate(routePath);
        }
    };

    const expandCurrentPageItems = useBoolean(true);
    let currentPageItemsInfo = parsePathnameToActiveMenuInfo(location.pathname);

    const newActiveItemsInfo = {
        topLevel: undefined,
        secondLevel: undefined,
        thirdLevel: undefined,
    };

    const showCollapsedMenuOverlay = useBoolean(false);
    const [activeItemsInfo, setActiveItemsInfo] = useState<ActiveItemPerLevelInfo>(currentPageItemsInfo);

    const monitoringToolkitItemIdRef = useRef<string>('');

    useEffect(() => {
        NavigationService.setSlideBarStatus(!isCollapsed.value);
    }, [isCollapsed.value]);

    useEffect(() => {
        if (studyRef) {
            loadConfigs({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef]);

    const [getStudyHasLegacyDatasets, { data: studyHasLegacyDatasets }] = useLazyQuery<
        studyHasDataSets,
        studyHasDataSetsVariables
    >(GET_STUDY_HAS_DATASETS);

    const [getStudyUsingCM2, { data: studyIsUsingCM2 }] = useLazyQuery<studyUsingCM2, studyUsingCM2Variables>(
        GET_STUDY_USING_CM2,
    );

    const [searchTerm, setSearchTerm] = useState<string>('');
    const expandedMenuItemsRef = useRef<string[]>([]);

    const searchActive = () => {
        return searchTerm !== '';
    };

    const [navigationItemList, setNavigationItemList] = useState(() => [
        ...pageNavItems({ dataView: dataViewNavigations }),
    ]);

    const filteredNavItems = filterPageNavItems(
        navigationItemList,
        studyHasLegacyDatasets?.studyHasDataSets ?? false,
        studyIsUsingCM2?.studyUsingCM2 ?? false,
        isCollapsed.value,
        user,
        state?.selectedStudy,
    );

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    const updateActiveItemsInfo = (
        itemId: string,
        level: number,
        params?: Record<string, string>,
        allowMenuItemToCollapse: boolean = true,
    ) => {
        const levelInfo: ActiveItemPerLevelInfo = {};
        // In order to display the pagenav with the relevant parent items styled and expanded correctly then the
        // activeItemsInfo needs to be updated sequentially. This approach was previously enforced through the UI
        // however the search functionality now allows the user to jump straight a child item without clicking on
        // the parent items. The code below finds all the parent items based on the level and updates the
        // activeItemsInfo sequentially to replicate the standard behaviour.
        switch (level) {
            case 2:
                const levelTwoTopTierItem = filteredNavItems.find((topTierItems) =>
                    topTierItems.items?.some((secondTierItems) =>
                        secondTierItems.items?.some((thirdTierItems) => thirdTierItems.key === itemId),
                    ),
                );
                const levelTwoSecondTierItem = levelTwoTopTierItem?.items?.find((secondTierItems) =>
                    secondTierItems.items?.some((thirdTierItems) => thirdTierItems.key === itemId),
                );
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                /* eslint-disable @eslint-react/set-state-in-effect */
                if (levelTwoTopTierItem?.key) {
                    setActiveItemsInfo(() => ({
                        ...levelInfo,
                        topLevel: { id: levelTwoTopTierItem?.key, params: undefined },
                    }));
                }
                if (levelTwoSecondTierItem?.key) {
                    setActiveItemsInfo((prevState) => ({
                        ...prevState,
                        secondLevel: { id: levelTwoSecondTierItem?.key, params: undefined },
                    }));
                }
                setActiveItemsInfo((prevState) => ({
                    ...prevState,
                    thirdLevel:
                        activeItemsInfo.thirdLevel?.id === itemId &&
                        activeItemsInfo.thirdLevel?.params === params &&
                        allowMenuItemToCollapse
                            ? undefined
                            : { id: itemId, params },
                }));
                /* eslint-enable @eslint-react/set-state-in-effect */
                break;
            case 1:
                const levelOneTopTierItem = filteredNavItems.find((topTierItems) =>
                    topTierItems.items?.some((secondTierItems) => secondTierItems.key === itemId),
                );
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                /* eslint-disable @eslint-react/set-state-in-effect */
                if (levelOneTopTierItem?.key) {
                    setActiveItemsInfo(() => ({
                        ...levelInfo,
                        topLevel: { id: levelOneTopTierItem?.key, params: undefined },
                    }));
                }
                setActiveItemsInfo((prevState) => ({
                    ...prevState,
                    secondLevel:
                        (activeItemsInfo.secondLevel?.id === itemId && allowMenuItemToCollapse) ||
                        (currentPageItemsInfo.secondLevel?.id === itemId && expandCurrentPageItems.value)
                            ? undefined
                            : { id: itemId, params },
                }));
                /* eslint-enable @eslint-react/set-state-in-effect */
                break;
            case 0:
            default:
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                // eslint-disable-next-line @eslint-react/set-state-in-effect
                setActiveItemsInfo(() => ({
                    ...levelInfo,
                    topLevel:
                        activeItemsInfo.topLevel?.id === itemId && allowMenuItemToCollapse
                            ? undefined
                            : { id: itemId, params },
                }));
                break;
        }
    };

    const updateActiveItemsAndNavigate = (
        itemId: string,
        level: number,
        hasSubItems: boolean,
        params?: Record<string, string>,
    ) => {
        if (isCollapsed.value && !showCollapsedMenuOverlay.value) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setActiveItemsInfo(getActiveItemsAfterItemChanged(itemId, level, newActiveItemsInfo, params));
        } else {
            updateActiveItemsInfo(itemId, level, params);
        }
        if (!hasSubItems) {
            navigateToRoute(itemId, params);
        }
    };

    const clearSearch = () => {
        expandedMenuItemsRef.current = [];
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setSearchTerm('');
        setNavigationItemList([...pageNavItems({ dataView: dataViewNavigations })]);
        // If the search box is cleared before the user clicks a navigation item then the pagenav is reset
        // to the previous state using the activeItemsInfo.
        if (activeItemsInfo.thirdLevel) {
            updateActiveItemsInfo(activeItemsInfo.thirdLevel.id, 2, activeItemsInfo.thirdLevel.params, false);
        } else if (activeItemsInfo.secondLevel) {
            updateActiveItemsInfo(activeItemsInfo.secondLevel.id, 1, activeItemsInfo.secondLevel.params, false);
        } else if (activeItemsInfo.topLevel) {
            updateActiveItemsInfo(activeItemsInfo.topLevel.id, 0, activeItemsInfo.topLevel.params, false);
        } else {
            setActiveItemsInfo(() => ({
                topLevel: undefined,
                secondLevel: undefined,
                thirdLevel: undefined,
            }));
        }
        /* eslint-enable @eslint-react/set-state-in-effect */
    };

    useEffect(() => {
        if (loading) return;
        const dataViewConfig = (configs?.dataViewConfigurations || [])
            ?.filter((x) => x?.inUse === true)
            .sort((a, b) => {
                return a!.displayIndex! - b!.displayIndex!;
            })
            ?.flatMap<DataViewConfigItem>((config) => {
                return config
                    ? [
                          {
                              name: config.name || '',
                              id: config.id as string,
                          },
                      ]
                    : [];
            });

        clearSearch();
        const activeMenu = parsePathnameToActiveMenuInfo(location.pathname);
        const activeDataViewConfig = dataViewConfig?.find((x) => x?.id === activeMenu?.thirdLevel?.params?.dataView);
        if (!activeDataViewConfig && dataViewConfig.length > 0 && activeMenu?.secondLevel?.id === 'cm2-data-views') {
            // One with the same id doesn't exist. Which we wouldn't expect when swapping studies
            // However one with the same name might exist. Check the local state, which still has the previous study
            // config. If a DV with the same insensitive name is found, use it
            const activeInPreviousStudy = state?.dataViewConfig?.find(
                (x) => x.id === activeMenu?.thirdLevel?.params?.dataView,
            );
            if (activeInPreviousStudy) {
                const matchinDV = dataViewConfig.find(
                    (x) =>
                        x?.name?.toLocaleLowerCase()?.trim() === activeInPreviousStudy.name.toLocaleLowerCase()?.trim(),
                );
                if (matchinDV) {
                    updateActiveItemsAndNavigate('cm2-data-views', 2, false, { dataView: matchinDV.id });
                }
            }
        }
        dispatch({ type: 'SET_DATA_VIEW_CONFIG', config: dataViewConfig });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [configs?.dataViewConfigurations, loading]);

    const activeTopItem = showCollapsedMenuOverlay.value
        ? filteredNavItems.find((item) => item.key === activeItemsInfo.topLevel?.id)
        : null;
    const changesWereMade = useBoolean(false);
    const handleNavRef = useRef<(() => void) | null>(null);
    const legacyChangesWereMade = useBoolean(false);

    useEffect(() => {
        isCollapsed.setValue(tabletMode);
        tabletMode && clearSearch();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [tabletMode]);

    const updateExpandedMenuItems = (
        menuItem: PageNavItem,
        currentExpandedMenuItems: string[],
        itemId?: string,
    ): string[] => {
        const expandedMenuItemIndex = currentExpandedMenuItems.indexOf(menuItem.key);
        if (menuItem.key === itemId) {
            if (expandedMenuItemIndex > -1) {
                menuItem.expandOnSearch = false;
                currentExpandedMenuItems = currentExpandedMenuItems.filter((i) => i !== menuItem.key);
            } else {
                menuItem.expandOnSearch = true;
                currentExpandedMenuItems.push(menuItem.key);
            }
        } else menuItem.expandOnSearch = expandedMenuItemIndex > -1;
        return currentExpandedMenuItems;
    };

    const getSearchResultItems = (
        filteredNavigationItems: PageNavItem[],
        searchValue: string,
        initiatedBySearchTermChange: boolean = false,
        itemId?: string,
    ): PageNavItem[] => {
        let currentExpandedMenuItems: string[] = initiatedBySearchTermChange ? [] : expandedMenuItemsRef.current;
        // Loop through all the top tier navigation items.
        filteredNavigationItems.forEach((topTierItem) => {
            // Does the top tier item contain the search term?
            const topTierLabelIncluded = t(topTierItem.label).toLowerCase().includes(searchValue);
            // Loop through all the second tier navigation items.
            topTierItem.items?.forEach((secondTierItem) => {
                // Does the second tier item contain the search term?
                const secondTierLabelIncluded = t(secondTierItem.label).toLowerCase().includes(searchValue);
                // Do not filter the third tier items if the search term exists in the top or second tier items
                // as we assume the user that might be looking for something under that item.
                // i.e. searching for the term 'System Admin' to then manually navigate to 'Manage Organizations'.
                if (!topTierLabelIncluded && !secondTierLabelIncluded) {
                    // If the top and second tier items do not contain the search term then we can assume that
                    // the user was not searching for those specific items and filter the third tier accordingly.
                    secondTierItem.items = secondTierItem.items?.filter((thirdTierItem) =>
                        t(thirdTierItem.label).toLowerCase().includes(searchValue),
                    );
                }
                // If the menu items are being updated due to the search term having changed.
                if (initiatedBySearchTermChange) {
                    // If any third tier items contain the search term.
                    if (
                        secondTierItem.items?.some((thirdTierItem) =>
                            t(thirdTierItem.label).toLowerCase().includes(searchValue),
                        )
                    ) {
                        // Expand the second tier menu item so that the relevant third tier items are visible
                        // and add it to the array of expanded items.
                        if (secondTierItem.items?.length ?? secondTierItem.items?.length > 0) {
                            secondTierItem.expandOnSearch = true;
                            const menuItemExists = currentExpandedMenuItems.includes(secondTierItem.key);
                            !menuItemExists && currentExpandedMenuItems.push(secondTierItem.key);
                        }
                    }
                    // If the menu items are being updated due a menu item being expanded or collapsed.
                } else {
                    // Ensure the selected menu item's collapsed state is flipped and all other menu item's states
                    // are preserved.
                    currentExpandedMenuItems = updateExpandedMenuItems(
                        secondTierItem,
                        currentExpandedMenuItems,
                        itemId,
                    );
                }
            });
            if (!topTierLabelIncluded) {
                // If the top tier item does not contain the search term then we can assume that the
                // user was not searching for that specific item and filter the second tier accordingly.
                // We also keep any second tier items which have a third tier item that matches the search term.
                // These third tier items will already have been filtered above.
                topTierItem.items = topTierItem.items?.filter(
                    (secondTierItem) =>
                        t(secondTierItem.label).toLowerCase().includes(searchValue) ||
                        secondTierItem.items?.some((thirdTierItem) =>
                            t(thirdTierItem.label).toLowerCase().includes(searchValue),
                        ),
                );
            }
            // If the menu items are being updated due to the search term having changed.
            if (initiatedBySearchTermChange) {
                // If any second or third tier items contain the search term then expand the top tier so
                // that the relevant items are visible.
                if (
                    topTierItem.items?.some((secondTierItem) =>
                        t(secondTierItem.label).toLowerCase().includes(searchValue),
                    ) ||
                    topTierItem.items?.some((secondTierItem) =>
                        secondTierItem.items?.some((thirdTierItem) =>
                            t(thirdTierItem.label).toLowerCase().includes(searchValue),
                        ),
                    )
                ) {
                    // Expand the top tier menu item so that the relevant second tier items are visible
                    // and add it to the array of expanded items.
                    if (topTierItem.items?.length && topTierItem.items?.length > 0) {
                        topTierItem.expandOnSearch = true;
                        const menuItemExists = currentExpandedMenuItems.includes(topTierItem.key);
                        !menuItemExists && currentExpandedMenuItems.push(topTierItem.key);
                    }
                }
                // If the menu items are being updated due a menu item being expanded or collapsed.
            } else {
                // Ensure the selected menu item's collapsed state is flipped and all other menu item's states
                // are preserved.
                currentExpandedMenuItems = updateExpandedMenuItems(topTierItem, currentExpandedMenuItems, itemId);
            }
        });
        expandedMenuItemsRef.current = currentExpandedMenuItems;
        return filteredNavigationItems;
    };

    const updateNavigationItemList = (searchValue: string, triggeredBySearch?: boolean, itemId?: string) => {
        const navItems: PageNavItem[] = [...pageNavItems({ dataView: dataViewNavigations })];
        // All top tier navigation items that contain the search term somewhere in the item's hierarchy.
        const filteredNavigationItems = navItems.filter(
            (ni) =>
                t(ni.label).toLowerCase().includes(searchValue) ||
                ni.items?.some((i) => t(i.label).toLowerCase().includes(searchValue)) ||
                ni.items?.some((i) => i.items?.some((j) => t(j.label).toLowerCase().includes(searchValue))),
        );
        const searchResultItems = getSearchResultItems(filteredNavigationItems, searchValue, triggeredBySearch, itemId);
        setNavigationItemList(searchResultItems);
    };

    const handleSearchTermChange = (event: TextBoxChangeEvent) => {
        const hasValue = event.target.value !== '';
        if (hasValue) {
            const searchValue = String(event.target.value).toLowerCase();
            setSearchTerm(searchValue);
            updateNavigationItemList(searchValue, true);
        } else {
            clearSearch();
        }
    };

    const onWarningLostDataDialogSubmit = () => {
        legacyChangesWereMade.setFalse();
        changesWereMade.setFalse();
        discardChanges();
        if (handleNavRef.current) {
            handleNavRef.current();
            handleNavRef.current = null;
        }
    };

    const checkShowWarningModal = async (previousMonitoringToolkitItemId?: string, optionalAction?: () => void) => {
        if (
            changesWereMade.value ||
            anyCentralMonitoringToolkitHasChanges() ||
            anySubjectMonitoringToolkitHasChanges() ||
            anyRiskAssessmentToolkitHasChanges() ||
            legacyChangesWereMade.value
        ) {
            // Need to set this regardless of outcome. If set after the return then navigation stops working.
            if (!!optionalAction) {
                handleNavRef.current = optionalAction;
            }

            const confirmed = legacyChangesWereMade.value
                ? await confirmDiscardChangesLegacy()
                : await confirmDiscardChanges('unsaved-changes-dialog');

            if (!confirmed) {
                // Reset the toolkit ID back to the previous one if the user decides not to discard changes
                if (previousMonitoringToolkitItemId) {
                    monitoringToolkitItemIdRef.current = previousMonitoringToolkitItemId;
                }
                return;
            }

            onWarningLostDataDialogSubmit();
        } else if (optionalAction && !changesWereMade.value) {
            optionalAction();
        }
    };

    const onMenuButtonClick = () => {
        clearSearch();
        LocalStorage.setItem(user!, 'PageNavIsCollapsed', !isCollapsed.value);
        isCollapsed.toggle();
        expandCurrentPageItems.setTrue();
        showCollapsedMenuOverlay.setFalse();
    };

    const onNavMenuItemClick = async (
        itemId: string,
        level: number,
        hasSubItems: boolean,
        params?: Record<string, string>,
    ) => {
        if (searchActive() && hasSubItems) {
            updateNavigationItemList(searchTerm, false, itemId);
        } else if (
            (level === 0 && (itemId !== activeItemsInfo.topLevel?.id || hasSubItems)) ||
            (level === 1 && (itemId !== activeItemsInfo.secondLevel?.id || hasSubItems)) ||
            (level === 2 &&
                (itemId !== activeItemsInfo.thirdLevel?.id ||
                    params !== activeItemsInfo.thirdLevel?.params ||
                    hasSubItems))
        ) {
            if (!hasSubItems) {
                clearSearch();
            }
            if (level === 0) {
                expandCurrentPageItems.setTrue();
            } else {
                expandCurrentPageItems.setFalse();
            }

            // We have an edge case where we need to know if we're switching from one toolkit to another.
            // Previously, we only had one toolkit, now we have two we need to treat it the same as changing routes.
            if (
                !monitoringToolkitItemIdRef.current &&
                (AllRoutes.isKeySubjectMonitoringToolkit(itemId) ||
                    AllRoutes.isKeyCentralMonitoringToolkit(itemId) ||
                    AllRoutes.isKeyRiskAssessmentToolkit(itemId))
            ) {
                monitoringToolkitItemIdRef.current = itemId;
            }

            let switchingToolkits = false;
            if (monitoringToolkitItemIdRef.current) {
                const currentId = monitoringToolkitItemIdRef.current;

                const isCentral = (id: string) => AllRoutes.isKeyCentralMonitoringToolkit(id) ?? false;
                const isSubject = (id: string) => AllRoutes.isKeySubjectMonitoringToolkit(id) ?? false;
                const isRisk = (id: string) => AllRoutes.isKeyRiskAssessmentToolkit(id) ?? false;

                const currentToolkitType = isCentral(currentId)
                    ? 'central'
                    : isSubject(currentId)
                      ? 'subject'
                      : isRisk(currentId)
                        ? 'risk'
                        : null;

                const nextToolkitType = isCentral(itemId)
                    ? 'central'
                    : isSubject(itemId)
                      ? 'subject'
                      : isRisk(itemId)
                        ? 'risk'
                        : null;

                switchingToolkits =
                    currentToolkitType !== null && nextToolkitType !== null && currentToolkitType !== nextToolkitType;
            }

            // Detect when we're switching between toolkits.
            // Need to reset back to the previous toolkit ID if the user clicks "Cancel" when asked to discard changes.
            let previousMonitoringToolkitItemId: string | undefined;
            if (
                AllRoutes.isKeySubjectMonitoringToolkit(itemId) ||
                AllRoutes.isKeyCentralMonitoringToolkit(itemId) ||
                AllRoutes.isKeyRiskAssessmentToolkit(itemId)
            ) {
                previousMonitoringToolkitItemId = monitoringToolkitItemIdRef.current;
                monitoringToolkitItemIdRef.current = itemId;
            }

            const monitoringToolkitChangesMade =
                anyCentralMonitoringToolkitHasChanges() ||
                anySubjectMonitoringToolkitHasChanges() ||
                anyRiskAssessmentToolkitHasChanges();
            const isMtk =
                AllRoutes.isKeyCentralMonitoringToolkit(itemId) ||
                AllRoutes.isKeySubjectMonitoringToolkit(itemId) ||
                AllRoutes.isKeyRiskAssessmentToolkit(itemId);

            if ((changesWereMade.value || legacyChangesWereMade.value) && !hasSubItems) {
                const updateNavFunc = () => updateActiveItemsAndNavigate(itemId, level, hasSubItems, params);
                await checkShowWarningModal(previousMonitoringToolkitItemId, updateNavFunc);
            } else if (!hasSubItems && monitoringToolkitChangesMade && (!isMtk || switchingToolkits)) {
                const updateNavFunc = () => updateActiveItemsAndNavigate(itemId, level, hasSubItems, params);
                await checkShowWarningModal(previousMonitoringToolkitItemId, updateNavFunc);
            } else {
                if (isCollapsed.value && hasSubItems) {
                    showCollapsedMenuOverlay.setTrue();
                }
                updateActiveItemsAndNavigate(itemId, level, hasSubItems, params);
                if (!hasSubItems) showCollapsedMenuOverlay.setFalse();
            }
        }
    };
    const root = document.documentElement;

    const calcMaxSubMenuHeight = () => {
        const topLevelMenuItemHeight = 40;
        const verticalHeightOffset = !searchActive() ? 224 : 264;
        const seedMenuItemVisible =
            (user?.systemAdminLevel ?? 0) >= Constants.SystemAdminLevel.Administrator &&
            window.environment !== Constants.environment.PROD;
        const includeSeedMenu = seedMenuItemVisible ? 1 : 0;
        const menuItemsTotal = filteredNavItems.length + includeSeedMenu;
        const gapTotal = 5 * (menuItemsTotal - 1);

        root.style.setProperty(
            '--max-pagenav-submenu-height',
            `max(calc(100vh - ${verticalHeightOffset + gapTotal + topLevelMenuItemHeight * menuItemsTotal}px), 100px)`,
        );
    };

    useEffect(() => {
        const subscribeUpdateWarningModal = NavigationService.legacyOnShowWarningLostDataModal().subscribe(
            (open: boolean) => {
                legacyChangesWereMade.setValue(open);
            },
        );
        return () => {
            subscribeUpdateWarningModal.unsubscribe();
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        calcMaxSubMenuHeight();
        const subscribeDefault = NavigationService.onShowWarningLostDataModal().subscribe((open) => {
            changesWereMade.setValue(open);
        });

        const subscribeOnViewViewRiskDetails = NavigationService.onViewRiskDetails().subscribe(() => {
            const itemsInfo = {
                topLevel: { id: 'risk-assessment-nav', params: undefined },
                secondLevel: { id: 'risk-log', params: undefined },
                thirdLevel: undefined,
            };
            setActiveItemsInfo(() => itemsInfo);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
            currentPageItemsInfo = itemsInfo;
        });

        const subscribeOnViewLegacySiteDashboard = NavigationService.onViewLegacySiteDashboard().subscribe(() => {
            const itemsInfo = {
                topLevel: { id: 'legacy-central-monitoring-nav', params: undefined },
                secondLevel: { id: 'cm-site-dashboard', params: undefined },
                thirdLevel: undefined,
            };
            setActiveItemsInfo(() => itemsInfo);
            currentPageItemsInfo = itemsInfo;
        });

        return () => {
            subscribeDefault.unsubscribe();
            discardChanges();
            subscribeOnViewLegacySiteDashboard.unsubscribe();
            subscribeOnViewViewRiskDetails.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (state?.selectedStudy?.id) {
            getStudyHasLegacyDatasets({
                variables: {
                    studyId: state.selectedStudy.id as string,
                },
            }).catch((err) => window.console.error(err));
            getStudyUsingCM2({
                variables: {
                    studyId: state.selectedStudy.id as string,
                },
            }).catch((err) => window.console.error(err));
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [state?.selectedStudy]);

    useEffect(() => {
        /**
         * We've navigated to this page programmatically from the subject hub,
         * so we must select the active item programmatically too.
         */
        if (state?.setSubjectProfileNavigation === true) {
            const subjectProfilePath = AllRoutes.path('sm-subject-profile');
            if (subjectProfilePath && updateActiveItemsInfo) {
                updateActiveItemsInfo(subjectProfilePath, 1);
            }
            if (dispatch) {
                // Clear the flag regardless of whether we could set the navigation or not
                dispatch({ type: 'SET_SUBJECT_PROFILE_NAVIGATION', setSubjectProfileNavigation: false });
            }
        }
    }, [dispatch, state?.setSubjectProfileNavigation, updateActiveItemsInfo]);

    return (
        <>
            <Drawer
                variant="permanent"
                className={cn(muiStyles.drawer, {
                    [muiStyles.drawerExpanded]: !isCollapsed.value,
                    [muiStyles.drawerCollapsed]: isCollapsed.value,
                })}
                classes={{
                    paper: cn('k-h-screen', styles.drawerPaper, styles.pageNavDrawerFont, {
                        [muiStyles.drawerExpanded]: !isCollapsed.value,
                        [muiStyles.drawerCollapsed]: isCollapsed.value,
                    }),
                }}
            >
                <div className={`k-overflow-hidden ${styles.drawerContent}`}>
                    <div
                        className={cn('k-w-full k-d-flex k-align-items-center', {
                            [styles.drawerHeader]: !isCollapsed.value,
                            'k-justify-content-between k-px-4': !isCollapsed.value,
                            'k-flex-col-reverse k-pt-5 k-px-2.5 k-gap-5': isCollapsed.value,
                        })}
                    >
                        <div className={isCollapsed.value ? '' : styles.drawerHeaderLightgray}>
                            {isCollapsed.value ? (
                                <img alt="logo" style={{ height: 42, width: 42 }} src={LogoLightgrayBackgroundImage} />
                            ) : (
                                <div className="k-d-flex k-align-items-center k-gap-2.5 k-mb-auto">
                                    <img alt="logo" className={styles.drawerHeaderLogo} src={LogoImage} />
                                    <OpraIcon role="img" title="logo-label" className="k-mb-2.5" />
                                </div>
                            )}
                        </div>
                        <div
                            className={cn(styles.drawerHeaderBottomGradient, {
                                'k-d-none': isCollapsed.value,
                            })}
                        />
                        {!isCollapsed.value && <div className={styles.drawerHeaderLightgrayTriangle} />}
                        <FontAwesomeIcon
                            className={cn('k-color-white k-cursor-pointer k-h-5 k-w-5', {
                                'k-pos-absolute k-right-4 k-z-10': !isCollapsed.value,
                            })}
                            style={{ top: '10.5px' }}
                            data-testid="expand-sidebar-button"
                            icon={['far', 'bars']}
                            onClick={onMenuButtonClick}
                        />
                        {!isCollapsed.value && (
                            <TextBox
                                className={styles.searchTextbox}
                                data-testid="nav-search-textbox"
                                fillMode="solid"
                                prefix={() => (
                                    <InputPrefix className="k-display-flex k-align-items-center k-color-white">
                                        <SvgIcon icon={searchIcon} size="large" style={{ padding: '3px' }} />
                                    </InputPrefix>
                                )}
                                placeholder="Search"
                                style={{ paddingLeft: '9px' }}
                                value={searchTerm}
                                autoFocus
                                onChange={handleSearchTermChange}
                            />
                        )}
                        {!isCollapsed.value && (
                            <>
                                <div className={styles.drawerHeaderDividerBlack} />
                                <div className={styles.drawerHeaderDividerLightblue} />
                            </>
                        )}
                    </div>
                    {isCollapsed.value && (
                        <div className="k-h-11 k-mt-1">
                            <PageNavTooltip title={t('Search')}>
                                <PageNavMenuItem
                                    isPageNavCollapsed={isCollapsed.value}
                                    icon={<SvgIcon className="k-color-white" icon={searchIcon} size="large" />}
                                    activeItemsInfo={activeItemsInfo}
                                    id="search-button"
                                    label={isCollapsed.value ? '' : t('Search')}
                                    itemKey="nav-search-button"
                                    onClick={onMenuButtonClick}
                                    searchActive={searchActive()}
                                    searchTerm={searchTerm}
                                    currentPageItemsInfo={currentPageItemsInfo}
                                    expandCurrentPageItems={expandCurrentPageItems}
                                />
                            </PageNavTooltip>
                        </div>
                    )}
                    {isCollapsed.value && (
                        <div
                            style={{
                                margin: '0 auto 8px auto',
                                height: '1px',
                                width: '40px',
                                backgroundColor: '#C9D3E1',
                            }}
                        />
                    )}
                    {searchActive() && (
                        <List disablePadding className={cn('k-d-flex k-flex-col k-gap-1 !k-mt-14')}>
                            <StyledMuiListItem
                                id="search-results-header"
                                className={cn(
                                    '!k-ml-4',
                                    '!k-mr-4',
                                    '!k-cursor-default',
                                    '!k-pr-3.5',
                                    navMenuItemStyles.firstLevelSelectedItem,
                                    navMenuItemStyles.listItem,
                                )}
                                disableRipple
                                data-testid="search-results-header"
                            >
                                <PageNavMenuItemIcon
                                    faIcon={['far', 'magnifying-glass']}
                                    className={cn('k-pl-0.5', '!k-cursor-default')}
                                    isCollapsed={isCollapsed.value}
                                />
                                <ListItemText
                                    className={cn('k-text-white', 'listItemText')}
                                    disableTypography
                                    primary={t('searchResults')}
                                />
                                <FontAwesomeIcon
                                    className={cn('k-color-white k-cursor-pointer k-h-5 k-w-5 k-z-10')}
                                    data-testid="search-results-header-button"
                                    icon={['far', 'arrow-left-long']}
                                    onClick={clearSearch}
                                />
                            </StyledMuiListItem>
                            <div className={cn(styles.searchResultsDivider)} />
                        </List>
                    )}
                    <div
                        className={cn({
                            [styles.drawerScroll]: !searchActive(),
                            [styles.searchResultsDrawerScroll]: searchActive(),
                            [styles.collapsedDrawerScroll]: isCollapsed.value,
                        })}
                    >
                        <List
                            disablePadding
                            className={cn({
                                [navMenuItemStyles.subMenu]: searchActive(),
                                [navMenuItemStyles.subMenuBorderBottom]: searchActive(),
                                [styles.searchSubMenu]: searchActive(),
                            })}
                        >
                            <TransitionGroup className={cn('k-d-flex k-flex-col k-gap-1')}>
                                {filteredNavItems?.map(({ key, ...navItem }) => {
                                    const navItemProps = {
                                        ...navItem,
                                        activeItemsInfo,
                                        onClick: onNavMenuItemClick,
                                        level: 0,
                                        id: key,
                                        label: isCollapsed.value ? '' : t(navItem.label),
                                        itemKey: `nav-${key}`,
                                        isPageNavCollapsed: isCollapsed.value,
                                        disabled: navItem.readOnly,
                                        tooltip: navItem.tooltip,
                                        searchActive: searchActive(),
                                        searchTerm,
                                        currentPageItemsInfo,
                                        expandCurrentPageItems,
                                    };

                                    const hasSubMenu = Array.isArray(navItem.items) && navItem.items.length > 0;
                                    const itemKey = `pagenav-main-li-${key}`;

                                    let content;

                                    // When searching the navigation menu only display menu items that are not readonly.
                                    if (searchActive()) {
                                        navItemProps.items = navItemProps.items?.filter((i) => !i.readOnly);
                                    }

                                    if (!(searchActive() && navItemProps.readOnly)) {
                                        if (hasSubMenu) {
                                            content = (
                                                <Collapse key={itemKey} timeout="auto">
                                                    <PageNavMenuItemWithSubMenu {...navItemProps} key={itemKey} />
                                                </Collapse>
                                            );
                                        } else {
                                            content = (
                                                <Collapse key={itemKey} timeout="auto">
                                                    <PageNavMenuItem {...navItemProps} key={itemKey} />
                                                </Collapse>
                                            );
                                        }
                                    }

                                    return content;
                                })}
                            </TransitionGroup>
                            <div className={cn('k-mt-1', { 'k-mb-1': !searchActive() })}>
                                <PageNavTooltip title={isCollapsed.value ? 'Reseed Data' : ''}>
                                    <ReseedData
                                        activeItemsInfo={activeItemsInfo}
                                        isPageNavCollapsed={isCollapsed.value}
                                        searchActive={searchActive()}
                                        searchTerm={searchTerm}
                                    />
                                </PageNavTooltip>
                            </div>
                        </List>
                    </div>
                </div>
                <PageNavFooter
                    isCollapsed={isCollapsed.value}
                    showCollapsedMenuOverlay={showCollapsedMenuOverlay}
                    setActiveItemsInfo={setActiveItemsInfo}
                />
            </Drawer>
            {showCollapsedMenuOverlay.value && !!activeTopItem?.items?.length && (
                <div className={cn(styles.collapsedMenuOverlay, styles.pageNavDrawerFont)}>
                    <PageNavMenuItemWithSubMenu
                        {...activeTopItem}
                        displayMode="collapsed"
                        id={activeTopItem.key}
                        activeItemsInfo={activeItemsInfo}
                        key={`pagenav-main-li-${activeTopItem.key}`}
                        onClick={onNavMenuItemClick}
                        searchActive={searchActive()}
                        searchTerm={searchTerm}
                        label={t(activeTopItem.label)}
                        itemKey={`nav-${activeTopItem.key}`}
                        currentPageItemsInfo={currentPageItemsInfo}
                        expandCurrentPageItems={expandCurrentPageItems}
                    />
                </div>
            )}
        </>
    );
};

export default PageNav;
